/**
 * Runtime drone connections.
 *
 * The fleet used to be fixed at boot (SIM_DRONES). These endpoints let an
 * operator point Parallax at a real radio, a SITL, or an autopilot bridge and
 * see it in the fleet the moment the link comes up — no restart, no config edit.
 */

import { api, type ApiResult } from '@/api/client';
import type { DroneId } from '@/types/domain';

export interface Connection {
  droneId: DroneId;
  callsign: string;
  /** Link state: 'connected' | 'connecting' | 'disconnected'. */
  state: string;
  /** The resolved link string, e.g. 'udpin:0.0.0.0:14550' or 'sim://virtual/SIM-1'. */
  link: string;
  simulated: boolean;
  sysid: number | null;
  lastMessageAt: number | null;
}

export interface AddConnection {
  /**
   * Optional only for a link listening on a team's port block: the server then
   * names the aircraft for the team (ALPHA_udp_3). Anywhere else it is required.
   */
  droneId?: string;
  callsign?: string;
  connectionString: string;
  sysid?: number;
}

/**
 * A free port in one of the caller's teams' blocks, and the name an aircraft
 * arriving on it will get. `port` is null when every port in the block already
 * carries an aircraft, which is a different thing to tell an operator from the
 * team having no block at all (then there is no offer).
 */
export interface PortOffer {
  teamId: string;
  callsign: string;
  transport: 'tcp' | 'udp';
  block: { start: number; end: number };
  inUse: number[];
  port: number | null;
  droneId: string | null;
  connectionString: string | null;
}

export async function getPortOffers(): Promise<ApiResult<PortOffer[]>> {
  const result = await api.get<{ offers: PortOffer[] }>('/connections/port-offer', {
    logged: false,
  });
  return result.ok ? { ...result, data: result.data.offers ?? [] } : result;
}

export async function listConnections(): Promise<ApiResult<Connection[]>> {
  const result = await api.get<{ connections: Connection[] }>('/connections', { logged: false });
  return result.ok ? { ...result, data: result.data.connections } : result;
}

export function addConnection(body: AddConnection): Promise<ApiResult<Connection>> {
  return api.post<Connection>('/connections', body, {
    label: `CONNECT ${body.droneId ?? body.connectionString}`,
  });
}

export function dropConnection(droneId: DroneId): Promise<ApiResult<{ removed: string }>> {
  return api.delete<{ removed: string }>(`/connections/${encodeURIComponent(droneId)}`, {
    label: `DISCONNECT ${droneId}`,
  });
}

/**
 * Aircraft heard announcing themselves, not yet part of the fleet.
 *
 * `listening: false` means discovery was never configured, which is a different
 * answer from "nothing is out there" — an operator staring at an empty list
 * deserves to know which.
 */
export interface DiscoveredAircraft {
  key: string;
  address: string;
  sysid: number;
  autopilot: number;
  vehicleType: number;
  heartbeats: number;
  ageS: number;
  /** The connection string it suggests — the translation where typos live. */
  suggestedLink: string;
}

export interface Discovery {
  listening: boolean;
  port: number | null;
  candidates: DiscoveredAircraft[];
}

export function listDiscovered(): Promise<ApiResult<Discovery>> {
  return api.get<Discovery>('/connections/discovered', { logged: false });
}

export function adoptDiscovered(body: {
  key: string;
  droneId: string;
  callsign?: string;
}): Promise<ApiResult<Connection>> {
  return api.post<Connection>('/connections/discovered/adopt', body, {
    label: `ADOPT ${body.droneId}`,
  });
}

/**
 * Where an aircraft's MAVLink is being copied to: other ground stations that
 * receive every frame it sends, one way, over UDP.
 */
export interface ForwardTarget {
  /** `ip:port`, which is also how a target is named to remove it. */
  target: string;
  ip: string;
  port: number;
}

export interface Forwarding {
  droneId: DroneId;
  targets: ForwardTarget[];
  framesSent: number;
  framesFailed: number;
}

export function getForwarding(droneId: DroneId): Promise<ApiResult<Forwarding>> {
  return api.get<Forwarding>(`/connections/${encodeURIComponent(droneId)}/forwarding`, {
    logged: false,
  });
}

export function addForwarding(
  droneId: DroneId,
  body: { host: string; port: number },
): Promise<ApiResult<Forwarding>> {
  return api.post<Forwarding>(`/connections/${encodeURIComponent(droneId)}/forwarding`, body, {
    label: `FORWARD ${droneId} TO ${body.host}:${body.port}`,
  });
}

export function removeForwarding(droneId: DroneId, target: string): Promise<ApiResult<Forwarding>> {
  return api.delete<Forwarding>(
    `/connections/${encodeURIComponent(droneId)}/forwarding/${encodeURIComponent(target)}`,
    { label: `STOP FORWARD ${droneId} TO ${target}` },
  );
}
